import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import {
  SecureCaseDocumentResponse,
  SecureCaseDocumentUrlResponse,
  SecureCaseNoteCreateRequest,
  SecureCaseNoteCreateResponse,
  SecureCaseNoteUpdateRequest,
  SecureCaseSuccessResponse,
  SecureCaseUploadInitiateRequest,
  SecureCaseUploadInitiateResponse,
  SecureCaseWorkspaceCreateRequest,
  SecureCaseWorkspaceCreateResponse,
  SecureCaseWorkspaceDetailResponse,
  SecureCaseWorkspaceLegalHoldRequest,
  SecureCaseWorkspaceListResponse,
  SecureCaseWorkspaceUpdateRequest,
} from '@chiroup/core/types/SecureCaseWorkspace.type';

const base = (clinicId: number, workspaceId?: string) =>
  `/secure-cases/${clinicId}${workspaceId ? `/${workspaceId}` : ''}`;

const secureCaseService = {
  list: (clinicId: number): Promise<SecureCaseWorkspaceListResponse> =>
    ChiroUpAPI.get('api', base(clinicId), {}),
  create: (
    clinicId: number,
    body: SecureCaseWorkspaceCreateRequest
  ): Promise<SecureCaseWorkspaceCreateResponse> => ChiroUpAPI.post('api', base(clinicId), { body }),
  get: (
    clinicId: number,
    workspaceId: string,
    syncSalesforce = true
  ): Promise<SecureCaseWorkspaceDetailResponse> =>
    ChiroUpAPI.get('api', `${base(clinicId, workspaceId)}?syncSalesforce=${syncSalesforce}`, {}),
  update: (
    clinicId: number,
    workspaceId: string,
    body: SecureCaseWorkspaceUpdateRequest
  ): Promise<SecureCaseSuccessResponse> =>
    ChiroUpAPI.patch('api', base(clinicId, workspaceId), { body }),
  createNote: (
    clinicId: number,
    workspaceId: string,
    body: SecureCaseNoteCreateRequest
  ): Promise<SecureCaseNoteCreateResponse> =>
    ChiroUpAPI.post('api', `${base(clinicId, workspaceId)}/notes`, { body }),
  updateNote: (
    clinicId: number,
    workspaceId: string,
    noteId: string,
    body: SecureCaseNoteUpdateRequest
  ): Promise<SecureCaseSuccessResponse> =>
    ChiroUpAPI.patch('api', `${base(clinicId, workspaceId)}/notes/${noteId}`, { body }),
  deleteNote: (
    clinicId: number,
    workspaceId: string,
    noteId: string
  ): Promise<SecureCaseSuccessResponse> =>
    ChiroUpAPI.del('api', `${base(clinicId, workspaceId)}/notes/${noteId}`, {}),
  initiateUpload: (
    clinicId: number,
    workspaceId: string,
    body: SecureCaseUploadInitiateRequest
  ): Promise<SecureCaseUploadInitiateResponse> =>
    ChiroUpAPI.post('api', `${base(clinicId, workspaceId)}/documents`, { body }),
  completeUpload: (
    clinicId: number,
    workspaceId: string,
    documentId: string
  ): Promise<SecureCaseDocumentResponse> =>
    ChiroUpAPI.post('api', `${base(clinicId, workspaceId)}/documents/${documentId}/complete`, {
      body: {},
    }),
  documentUrl: (
    clinicId: number,
    workspaceId: string,
    documentId: string,
    disposition: 'inline' | 'attachment'
  ): Promise<SecureCaseDocumentUrlResponse> =>
    ChiroUpAPI.get(
      'api',
      `${base(clinicId, workspaceId)}/documents/${documentId}/url?disposition=${disposition}`,
      {}
    ),
  deleteDocument: (
    clinicId: number,
    workspaceId: string,
    documentId: string
  ): Promise<SecureCaseSuccessResponse> =>
    ChiroUpAPI.del('api', `${base(clinicId, workspaceId)}/documents/${documentId}`, {}),
  legalHold: (
    clinicId: number,
    workspaceId: string,
    body: SecureCaseWorkspaceLegalHoldRequest
  ): Promise<SecureCaseSuccessResponse> =>
    ChiroUpAPI.put('api', `${base(clinicId, workspaceId)}/legal-hold`, { body }),
};

export default secureCaseService;
